import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  X
} from 'lucide-react';
import io from 'socket.io-client';

const ResumeParsingStatus = ({ onClose }) => {
  const [activeJobs, setActiveJobs] = useState([]);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Initialize Socket.IO connection
    const newSocket = io(process.env.REACT_APP_API_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('🔌 Connected to server for status tracking');
      setIsConnected(true);
      
      // Subscribe to all user jobs
      newSocket.emit('subscribe-progress', { 
        userId: localStorage.getItem('userId') 
      });
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Disconnected from server');
      setIsConnected(false);
    });

    newSocket.on('progress-update', (jobData) => {
      console.log('📊 Status update received:', jobData);
      setActiveJobs(prev => {
        const existing = prev.find(job => job.id === jobData.id);
        if (existing) {
          return prev.map(job => job.id === jobData.id ? jobData : job);
        } else {
          return [...prev, jobData];
        }
      });
    });

    newSocket.on('job-completed', (jobData) => {
      console.log('✅ Job completed:', jobData);
      setActiveJobs(prev => prev.map(job => job.id === jobData.id ? jobData : job));
      
      // Remove completed job after 5 seconds
      setTimeout(() => {
        setActiveJobs(prev => prev.filter(job => job.id !== jobData.id));
      }, 5000);
    });

    newSocket.on('job-failed', (jobData) => {
      console.log('❌ Job failed:', jobData);
      setActiveJobs(prev => prev.map(job => job.id === jobData.id ? jobData : job));
      
      // Remove failed job after 10 seconds
      setTimeout(() => {
        setActiveJobs(prev => prev.filter(job => job.id !== jobData.id));
      }, 10000);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'started':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'started':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-600';
    }
  };

  if (activeJobs.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-4 right-4 z-50 space-y-3"
      >
        {activeJobs.map((job) => (
          <motion.div
            key={job.id}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className={`p-4 rounded-xl border-2 shadow-lg max-w-sm ${getStatusColor(job.status)}`}
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {getStatusIcon(job.status)}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm">Resume Parsing</h4>
                <p className="text-xs opacity-75 truncate">
                  {job.currentMessage || 'Processing...'}
                </p>
                {job.status === 'started' && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div 
                        className="h-1 bg-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => setActiveJobs(prev => prev.filter(j => j.id !== job.id))}
                className="flex-shrink-0 p-1 hover:bg-black hover:bg-opacity-10 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

export default ResumeParsingStatus;
